import isNil from 'lodash-es/isNil';
import omitBy from 'lodash-es/omitBy';
import type { FieldErrors, FieldPath, FieldValues } from 'react-hook-form';
import * as yup from 'yup';

export const getYupSchemaFlatKeys = (schema, parentKey = '') => {
  let keys: string[] = [];

  if (schema?.fields) {
    for (const key in schema.fields) {
      const newKey = parentKey ? `${parentKey}.${key}` : key;

      // @ts-ignore
      if (schema.fields[key]?.fields) {
        // @ts-ignore
        keys = keys.concat(getYupSchemaFlatKeys(schema.fields[key], newKey));
      } else {
        keys.push(newKey);
      }
    }
  }

  return keys;
};

export const mergeNestedSchemas = (...schemas: yup.ObjectSchema<any>[]): yup.ObjectSchema<any> => {
  const mergedShape: Record<string, any> = {};

  for (const schema of schemas) {
    for (const key in schema.fields) {
      const field = schema.fields[key];
      if (
        mergedShape[key] &&
        field instanceof yup.ObjectSchema &&
        mergedShape[key] instanceof yup.ObjectSchema
      ) {
        // recursively merge nested object schemas (e.g., mapModel)
        mergedShape[key] = mergeNestedSchemas(mergedShape[key], field as yup.ObjectSchema<any>);
      } else {
        mergedShape[key] = field;
      }
    }
  }

  return yup.object().shape(mergedShape);
};

// Todo: logic is not strict and return value is unreliable
export const getErrorMsgFromFieldErrors = (errors?: FieldErrors) => {
  if (!errors) return;
  const firstKey = Object.keys(errors)[0];
  if (errors[firstKey]) {
    if (errors[firstKey]?.message) return errors[firstKey]?.message;
    return getErrorMsgFromFieldErrors(errors[firstKey] as FieldErrors);
  }
};

export function yupToCompatErrors<T extends FieldValues>(err: yup.ValidationError) {
  return err.inner.map((item) => {
    const field = item.path as FieldPath<T>;
    const match = field.match(/\[(\d+)\]/);
    const index = match ? Number(match[1]) : null;
    return {
      path: item.path as FieldPath<T>,
      message: item.message,
      index,
    };
  });
}

export function formatPrefix(label: string) {
  return `[${label}]`;
}

export function formatValueInvalid(prefix: string, v: string, quote = true) {
  return `${prefix} ${quote ? JSON.stringify(v) : v} is invalid`;
}

export function noop() {}

export function deferred<T>() {
  let resolve: (value: T | PromiseLike<T>) => void = noop;
  let reject: (reason?: any) => void = noop;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return {
    promise,
    resolve,
    reject,
  };
}

export const capitalizeFirstLetter = (str) => str?.[0]?.toUpperCase() + str?.slice(1) || '';

export const omitNil = <T extends object>(data: T) => omitBy(data, isNil) as Required<T>;

export * from './lru';
export * from './validate';
